// NOTE: Namespace import is intentional — custom entity types can reference any icon
// by name from the API/database, requiring runtime resolution against the full icon set.
// This prevents tree-shaking for @untitledui/icons. If bundle size becomes a concern,
// consider switching to dynamic import() with React.lazy/Suspense.
import * as UntitledIcons from '@untitledui/icons';
import {
  AlertTriangle,
  AlignTopArrow02,
  BarChart01,
  Briefcase01,
  Calendar,
  ClockRewind,
  Container,
  Fingerprint03,
  Folder,
  Grid01,
  Package,
  User01,
  Users01,
  Zap,
} from '@untitledui/icons';

import { PipelineIcon, WebhookIcon } from './CustomIcons';

interface EntityTypeIconProps {
  /** Entity type name (e.g., ahoy_contact) — used as fallback when no icon name is provided */
  entityTypeName: string;
  /** Icon name from @untitledui/icons (e.g., "User01", "Building07") — takes priority over entityTypeName */
  iconName?: string;
  className?: string;
}

/**
 * Icon component type matching the pattern used in UI components.
 * Compatible with @untitledui/icons and badge IconComponentType.
 */
export type EntityIconComponent = React.FunctionComponent<{
  className?: string;
  strokeWidth?: string | number;
}>;

/**
 * Resolve an icon component by its @untitledui/icons name (e.g., "User01", "Building07").
 * Returns Folder as fallback if name is not found.
 */
/** Custom icon components not available in @untitledui/icons. */
const CUSTOM_ICON_MAP: Record<string, EntityIconComponent> = {
  WebhookIcon,
  PipelineIcon,
};

// eslint-disable-next-line react-refresh/only-export-components -- icon utility function co-located with icon component
export function getIconComponentByName(iconName: string): EntityIconComponent {
  const custom = CUSTOM_ICON_MAP[iconName];
  if (custom) {
    return custom;
  }
  const component = (UntitledIcons as Record<string, unknown>)[iconName];
  if (typeof component === 'function') {
    return component as EntityIconComponent;
  }
  return Folder;
}

/**
 * Dynamic icon registry populated at runtime from the object types API.
 * Components that fetch object types (e.g., SideNavigation, GenericCrmList)
 * call registerEntityTypeIcons() to populate this cache, making icons available
 * to all downstream components (avatars, badges, cards, etc.) without prop drilling.
 */
const dynamicIconRegistry = new Map<string, string>();

/**
 * Register entity type → icon mappings from the object types API.
 * Call this after fetching object types so that getEntityTypeIconComponent
 * can resolve icons for custom entity types.
 */
// eslint-disable-next-line react-refresh/only-export-components -- icon registry function co-located with icon component
export function registerEntityTypeIcons(icons: Record<string, string>): void {
  dynamicIconRegistry.clear();
  for (const [entityTypeName, iconName] of Object.entries(icons)) {
    if (iconName) {
      dynamicIconRegistry.set(entityTypeName, iconName);
    }
  }
}

/** Custom icon components for entity types not covered by @untitledui/icons. */
const CUSTOM_ICONS: Record<string, EntityIconComponent> = {
  ahoy_automation_webhook: WebhookIcon,
};

/**
 * Known entity kinds, matching the backend registry
 * (apps/crm_events/schema_registry/entity_kinds.py). Kept in lockstep with
 * ENTITY_KIND_ICONS and KIND_ICON_PRECEDENCE below via compile-time exhaustiveness
 * checks — a kind added to the backend registry but missing from either map is a
 * type error here, not a silent runtime fallback to Container.
 */
type EntityKind =
  | 'person'
  | 'party'
  | 'principal'
  | 'case'
  | 'asset'
  | 'activity'
  | 'signal'
  | 'event'
  | 'pipeline'
  | 'stage'
  | 'reporting'
  | 'automation'
  | 'default'
  | 'empty'
  | 'any';

/**
 * Builds a function that checks its argument is an array containing every member of T,
 * with no omissions — unlike a plain `readonly T[]` annotation, which only constrains
 * each element and does not catch a missing member.
 */
function arrayOfAll<T extends string>() {
  return <U extends readonly T[]>(
    array: U & ([T] extends [U[number]] ? unknown : never)
  ) => array;
}

/**
 * Icon for each entity kind, matching the backend registry
 * (apps/crm_events/schema_registry/entity_kinds.py). `default` and `empty` deliberately
 * map to Container — they are the non-descriptive composable hosts, so a type whose
 * ONLY kind is `default` (assigned by the create-object-type wizard, see
 * buildObjectTypeEvents.ts) keeps today's generic glyph. `any` is the special
 * association-only kind (KIND_ANY in associationMatching.ts) and never appears in an
 * entity type's entity_kind tuple.
 */
const ENTITY_KIND_ICONS = {
  person: User01,
  party: Users01,
  // Fingerprint = "an identity that acts" — deliberately not a person glyph
  // (person/party own those) and not Shield (reads as security/protection).
  principal: Fingerprint03,
  case: Briefcase01,
  asset: Package,
  activity: ClockRewind,
  signal: AlertTriangle,
  event: Calendar,
  pipeline: PipelineIcon,
  stage: AlignTopArrow02,
  reporting: BarChart01,
  automation: Zap,
  default: Container,
  empty: Container,
  any: Grid01,
} satisfies Record<EntityKind, EntityIconComponent>;

/**
 * Deterministic precedence order for picking ONE icon from a multi-kind tuple
 * (kinds compose, e.g. ['party', 'person']). Most-specific first. `any` is
 * intentionally excluded — it never appears in an entity type's entity_kind tuple.
 */
const KIND_ICON_PRECEDENCE = arrayOfAll<Exclude<EntityKind, 'any'>>()([
  'person',
  'party',
  'principal',
  'case',
  'asset',
  'activity',
  'signal',
  'event',
  'pipeline',
  'stage',
  'reporting',
  'automation',
  'default',
  'empty',
]);

/**
 * Dynamic kind registry populated at runtime from the object types API, mapping
 * entity_type_name -> entity_kind[]. See dynamicIconRegistry above for the same pattern.
 */
const dynamicKindRegistry = new Map<string, readonly string[]>();

/**
 * Register entity type -> entity_kind[] mappings from the object types API.
 * Call this after fetching object types so that getEntityTypeIconComponent
 * can fall back to a kind-based icon for custom entity types with no icon of their own.
 */
// eslint-disable-next-line react-refresh/only-export-components -- icon registry function co-located with icon component
export function registerEntityTypeKinds(
  kinds: Record<string, readonly string[]>
): void {
  dynamicKindRegistry.clear();
  for (const [entityTypeName, entityKinds] of Object.entries(kinds)) {
    if (entityKinds.length > 0) {
      dynamicKindRegistry.set(entityTypeName, entityKinds);
    }
  }
}

/**
 * Resolve the highest-precedence icon for a tuple of entity kinds.
 * Returns undefined when entityKinds is empty or contains nothing recognised.
 */
function resolveKindIcon(
  entityKinds: readonly string[]
): EntityIconComponent | undefined {
  for (const kind of KIND_ICON_PRECEDENCE) {
    if (entityKinds.includes(kind)) {
      return ENTITY_KIND_ICONS[kind];
    }
  }
  return undefined;
}

/**
 * Gets the icon component for a given entity kind name.
 */
// eslint-disable-next-line react-refresh/only-export-components -- icon utility function co-located with icon component
export function getEntityKindIconComponent(
  kindName: string
): EntityIconComponent {
  return ENTITY_KIND_ICONS[kindName as EntityKind] ?? Container;
}

/** Hardcoded fallback map for when icon name is not available from the API. */
const FALLBACK_ICONS: Record<string, string> = {
  ahoy_contact: 'User01',
  ahoy_company: 'Building07',
  ahoy_deal: 'Target04',
  ahoy_deal_pipeline: 'PipelineIcon',
  ahoy_note: 'StickerSquare',
  ahoy_task: 'CheckDone01',
  ahoy_reminder: 'BellRinging01',
  ahoy_user: 'Users01',
  ahoy_email_thread: 'Mail01',
  ahoy_deal_pipeline_stage: 'AlignTopArrow02',
  ahoy_contact_lifecycle_stage: 'UserUp01',
  ahoy_meeting: 'Calendar',
  ahoy_import: 'Download01',
  ahoy_external_integration: 'PuzzlePiece01',
  ahoy_risk: 'AlertTriangle',
  // Both automation types share the single automation glyph (matches the nav's Dataflow03).
  ahoy_event_automation: 'Dataflow03',
  ahoy_scheduled_automation: 'Dataflow03',
  ahoy_validation_rule: 'ShieldTick',
  // Keep in sync with the backend ahoy_agent entity_types.yaml icon so the
  // record header shows UserCircle immediately, before the object-types API
  // loads. (Zap belongs to automations — the agent is a distinct actor.)
  ahoy_agent: 'UserCircle',
  // Meta entity-type used for property-reference chips in read-only formulas
  // (see FormulaDisplay's META_PROPERTY_TYPE). Keep in sync with the backend
  // entity_types.yaml icon so the chip matches before the object-types API loads.
  ahoy_property_type: 'TextInput',
};

/**
 * Gets the icon component for a given entity type.
 *
 * Resolution order:
 * 1. Explicit iconName parameter (highest priority)
 * 2. Dynamic registry (populated from object types API at runtime)
 * 3. Hardcoded FALLBACK_ICONS (for system types when API hasn't loaded yet)
 * 4. Kind-based icon — resolved from the explicit entityKinds argument (takes
 *    precedence so callers that already hold the schema don't race the registry,
 *    which is populated in a useEffect) or, failing that, the dynamic kind registry
 * 5. Container icon (default fallback — covers "kinds unknown / API not loaded")
 */
// eslint-disable-next-line react-refresh/only-export-components -- icon utility function co-located with icon component
export function getEntityTypeIconComponent(
  entityTypeName: string,
  iconName?: string,
  entityKinds?: readonly string[]
): EntityIconComponent {
  // Check custom icons first (SVGs not in @untitledui/icons)
  if (!iconName && CUSTOM_ICONS[entityTypeName]) {
    return CUSTOM_ICONS[entityTypeName];
  }
  const name =
    iconName ||
    dynamicIconRegistry.get(entityTypeName) ||
    FALLBACK_ICONS[entityTypeName];
  if (name) {
    return getIconComponentByName(name);
  }
  const kindIcon = resolveKindIcon(
    entityKinds ?? dynamicKindRegistry.get(entityTypeName) ?? []
  );
  if (kindIcon) {
    return kindIcon;
  }
  return Container;
}

/**
 * Resolve the icon for an association target — the single helper every association
 * call site should use so no call site needs a nested ternary.
 */
// eslint-disable-next-line react-refresh/only-export-components -- icon utility function co-located with icon component
export function getAssociationTargetIconComponent(target: {
  target_entity_type_name?: string | null;
  target_entity_kind_name?: string | null;
}): EntityIconComponent {
  if (target.target_entity_type_name) {
    return getEntityTypeIconComponent(
      target.target_entity_type_name,
      undefined,
      target.target_entity_kind_name
        ? [target.target_entity_kind_name]
        : undefined
    );
  }
  if (target.target_entity_kind_name) {
    return getEntityKindIconComponent(target.target_entity_kind_name);
  }
  return Container;
}

/**
 * Centralized icon component for CRM entity types.
 */
export function EntityTypeIcon({
  entityTypeName,
  iconName,
  className,
}: EntityTypeIconProps) {
  const IconComponent = getEntityTypeIconComponent(entityTypeName, iconName);
  return <IconComponent className={className} />;
}
